/**
 * Please look at the different grid providers os and browser version support before changing:
 * LT - https://www.lambdatest.com/support/docs/supported-browsers-and-operating-systems-for-the-web-interface/#desktop-devices-for-selenium--browser-versions
 * BS - https://www.browserstack.com/list-of-browsers-and-platforms?product=automate
 * SAUCE - https://wiki.saucelabs.com/display/DOCS/Platform+Configurator#/
 */
import * as os from 'os';
import * as fs from 'fs';
import crypto from 'crypto';
import * as _ from 'lodash';
import * as path from 'path';
import { DOMParser } from 'linkedom';
import { W3C_ELEMENT_ID } from '../player/constants';
import type { RawTestResult } from 'services/src/commons/mongo/models/testResult';
import type { RunnerOptions } from 'src/runOptions';

const HOMEDIR = os.homedir();
export const TESTIM_BROWSER_DIR = path.join(HOMEDIR, '.testim-browser-profile');
const DEFAULT_CHROME_VERSON = '133';

export const OSS = [
    { osName: 'Linux', bs: { os: 'LINUX' }, sl: { platform: 'Linux' }, lt: { platform: 'Linux' } },
    {
        osName: 'Windows 11',
        bs: { os: 'WINDOWS', os_version: '11' },
        sl: { platform: 'Windows 11' },
        lt: { platform: 'Windows 11' },
    },
    {
        osName: 'Windows 10',
        bs: { os: 'WINDOWS', os_version: '10' },
        sl: { platform: 'Windows 10' },
        lt: { platform: 'Windows 10' },
    },
    { osName: 'Windows 8', bs: { os: 'WINDOWS', os_version: '8' }, sl: { platform: 'Windows 8' } },
    { osName: 'Windows 8.1', bs: { os: 'WINDOWS', os_version: '8.1' }, sl: { platform: 'Windows 8.1' } },
    { osName: 'Windows 7', bs: { os: 'WINDOWS', os_version: '7' }, sl: { platform: 'Windows 7' } },
    { osName: 'Windows XP', bs: { os: 'WINDOWS', os_version: 'XP' }, sl: { platform: 'Windows XP' } },
    {
        osName: 'MacOS Tahoe',
        bs: { os: 'OS X', os_version: 'Tahoe', safari_version: '26.2' },
        sl: {},
        lt: { platform: 'MacOS Tahoe' },
    },
    {
        osName: 'MacOS Sequoia',
        bs: { os: 'OS X', os_version: 'Sequoia', safari_version: '18.4' },
        sl: { platform: 'macOS 15', safari_version: 'latest' },
        lt: { platform: 'MacOS Sequoia' },
    },
    {
        osName: 'macOS Sonoma',
        bs: { os: 'OS X', os_version: 'Sonoma', safari_version: '17.3' },
        sl: { platform: 'macOS 14', safari_version: 'latest' },
        lt: { platform: 'macOS Sonoma' },
    },
    {
        osName: 'macOS Ventura',
        bs: { os: 'OS X', os_version: 'Ventura', safari_version: '16.5' },
        sl: { platform: 'macOS 13', safari_version: 'latest' },
        lt: { platform: 'macOS Ventura' },
    },
    {
        osName: 'macOS Monterey',
        bs: { os: 'OS X', os_version: 'Monterey', safari_version: '15.6' },
        sl: { platform: 'macOS 12', safari_version: 'latest' },
        lt: { platform: 'macOS Monterey' },
    },
    {
        osName: 'macOS Big Sur',
        bs: { os: 'OS X', os_version: 'Big Sur', safari_version: '14.1' },
        sl: { platform: 'macOS 11', safari_version: 'latest' },
        lt: { platform: 'macOS Big Sur' },
    },
    {
        osName: 'macOS Catalina',
        bs: { os: 'OS X', os_version: 'Catalina', safari_version: '13.1' },
        sl: {},
        lt: { platform: 'macOS Catalina' },
    },
    {
        osName: 'macOS Mojave',
        bs: { os: 'OS X', os_version: 'Mojave', safari_version: '12.1' },
        sl: {},
        lt: { platform: 'macOS Mojave' },
    },
    {
        osName: 'macOS High Sierra',
        bs: { os: 'OS X', os_version: 'High Sierra', safari_version: '11.1' },
        sl: {},
        lt: { platform: 'macOS High Sierra' },
    },
    {
        osName: 'macOS Sierra',
        bs: { os: 'OS X', os_version: 'Sierra', safari_version: '10.1' },
        sl: {},
        lt: { platform: 'macOS Sierra' },
    },
    {
        osName: 'OS X El Capitan',
        bs: { os: 'OS X', os_version: 'El Capitan', safari_version: '9.1' },
        sl: {},
        lt: { platform: 'OS X El Capitan' },
    },
    {
        osName: 'OS X Yosemite',
        bs: { os: 'OS X', os_version: 'Yosemite', safari_version: '8' },
        sl: {},
        lt: { platform: 'OS X Yosemite' },
    },
    { osName: 'OS X Mavericks', bs: { os: 'OS X', os_version: 'Mavericks', safari_version: '7.1' }, sl: {} },
    {
        osName: 'OS X Mountain Lion',
        bs: { os: 'OS X', os_version: 'Mountain Lion', safari_version: '6.2' },
        sl: {},
        lt: { platform: 'OS X Mountain Lion' },
    },
    { osName: 'OS X Lion', bs: { os: 'OS X', os_version: 'Lion', safari_version: '6' }, sl: {} },
    { osName: 'OS X Snow Leopard', bs: { os: 'OS X', os_version: 'Snow Leopard', safari_version: '5.1' }, sl: {} },

    { osName: 'iOS', bs: { platform: 'MAC' }, sl: { platformName: 'iOS', appiumVersion: '1.6.4' } },
    { osName: 'Android', bs: { platform: 'ANDROID' }, sl: { platformName: 'Android', appiumVersion: '1.6.4' } },
];

const BROWSERS = [
    {
        browserName: 'Chrome',
        bs: { browser: 'Chrome', browser_version: DEFAULT_CHROME_VERSON },
        sl: { browserName: 'chrome', version: '94.0' },
        browserValue: 'chrome',
    },
    {
        browserName: 'Firefox',
        bs: { browser: 'Firefox', browser_version: '89' },
        sl: { browserName: 'firefox', version: '89.0' },
        browserValue: 'firefox',
    },
    { browserName: 'Safari', bs: { browser: 'Safari' }, sl: { browserName: 'safari' }, browserValue: 'safari' },
     
    {
        browserName: 'Edge Chromium',
        bs: { browser: 'Edge', browser_version: DEFAULT_CHROME_VERSON },
        sl: { browserName: 'MicrosoftEdge', version: '94' },
        synonyms: ['edge-chromium'],
        browserValue: 'edge-chromium',
        seleniumName: 'MicrosoftEdge',
    },
    { browserName: 'Browser', bs: {}, sl: { browserName: 'Browser' }, browserValue: 'browser' },
    { browserName: 'Android', bs: { browserName: 'android' }, sl: {}, browserValue: 'android' },
    { browserName: 'iPad', bs: { browserName: 'iPad' }, sl: {}, browserValue: 'ipad' },
    { browserName: 'iPhone', bs: { browserName: 'iPhone' }, sl: {}, browserValue: 'iphone' },
];

export function sanitizeNumberValue(val: string | number, defaultVal: number) {
    const sanitizedValue = Number(val);
    if (Number.isNaN(sanitizedValue) || sanitizedValue <= 0) {
        return defaultVal;
    }
    return sanitizedValue;
}

export function getBrowserInfo(browserValue: string) {
    browserValue = browserValue.toLowerCase();
    return BROWSERS.find((b) => b.browserValue === browserValue);
}

export function getRunConfigByBrowserName(
    browser: string,
    saucelabs: RunnerOptions['saucelabs'] | undefined,
    browserstack: RunnerOptions['browserstack'] | undefined,
) {
    browser = browser?.toLowerCase();
    // @ts-expect-error the `.include(b.synonyms)` looks like a mistake, it cant be true
    const selectedBrowser = BROWSERS.find(b => b.browserName.toLowerCase() === browser || browser.includes(b.synonyms)) || BROWSERS[0];

    // BS and SL do not support Linux for newer browser, so use Windows instead.
    let selectedOS = OSS.find((x) => x.osName === 'Windows 10');
    if (saucelabs) {
        if (saucelabs.platform) {
            selectedOS = OSS.find((o) => (o.sl as { platform?: string }).platform === saucelabs.platform);
        } else if (saucelabs.platformName) {
            selectedOS = OSS.find((o) => (o.sl as { platformName?: string }).platformName === saucelabs.platformName);
        }
    }
    if (browserstack) {
        if (browserstack.os_version) {
            selectedOS = OSS.find((o) => (o.bs as { os_version?: string }).os_version === browserstack.os_version);
        } else if (browserstack.platform) {
            selectedOS = OSS.find((o) => (o.bs as { platform?: string }).platform === browserstack.platform);
        }
    }

    return _.merge(selectedBrowser, selectedOS);
}

export function getEnvironmentGitBranch() {
    return process.env.GIT_BRANCH || process.env.CIRCLE_BRANCH || process.env.TRAVIS_BRANCH || process.env.CI_BRANCH || process.env.GITHUB_BRANCH_NAME;
}

export function removePropertyFromObject(
    obj: object,
    propName: string,
    cmpFunction: (value: any, other: any) => boolean,
) {
    // eslint-disable-next-line no-restricted-syntax
    for (const prop in obj) {
        if (Object.hasOwn(obj, prop)) {
            if (cmpFunction(prop as any, propName)) {
                delete obj[prop as keyof typeof obj];
            } else if (typeof obj[prop as keyof typeof obj] === 'object') {
                removePropertyFromObject(obj[prop as keyof typeof obj], propName, cmpFunction);
            }
        }
    }
}

export interface W3CElement {
    [W3C_ELEMENT_ID]: string;
}
export interface LegacyElement {
    ELEMENT: string;
}
export function extractElementId(element: W3CElement | LegacyElement) {
    return (element as LegacyElement).ELEMENT || (element as W3CElement)[W3C_ELEMENT_ID];
}

export function getPlanType(plan: { expireAt?: number; expireAT?: number; plan?: string }) {
    plan ||= {};
    const now = Date.now();
    const expirationDate = plan.expireAt || plan.expireAT;

    if (plan.plan !== 'free') {
        return 'pro';
    }
    if (expirationDate) {
        return expirationDate < now ? 'free' : 'trial';
    }
    return 'free';
}

export const calcPercentile = (arr: number[], percentile: number) => {
    if (arr.length === 0) return 0;
    if (typeof percentile !== 'number') throw new TypeError('p must be a number');

    arr = [...arr].sort((a, b) => a - b);

    if (percentile <= 0) return arr[0];
    if (percentile >= 100) return arr.at(-1);

    const index = Math.ceil(arr.length * (percentile / 100)) - 1;
    return arr[index];
};

interface TestResultWithRetries extends RawTestResult {
    retryTestResults: RawTestResult[];
}

// NOTE: This duplicates a function in services (stream-data/result/resultService.ts) since we can't share code between packages.
export function groupTestsByRetries(testResults: RawTestResult[] = []) {
    return (
        _.chain(testResults)
            .groupBy((tr) => tr.originalTestResultId || tr.resultId)
            .values()
            // Should be flatMap
            .reduce<RawTestResult[]>((all, current) => {
                if (!current) {
                    return all;
                }
                if (current.length === 1) {
                    all.push(current[0]);
                    return all;
                }
                const sorted = [...current].sort((a, b) => {
                    const aCount = typeof a.retryCount === 'number' ? a.retryCount : 1;
                    const bCount = typeof b.retryCount === 'number' ? b.retryCount : 1;
                    if (aCount === bCount) {
                        return a.startTime - b.startTime;
                    }
                    return (a.retryCount ?? 0) - (b.retryCount ?? 0);
                });
                const last = _.cloneDeep(sorted.at(-1)) as TestResultWithRetries | undefined;
                if (!last) {
                    return all;
                }
                last.retryTestResults = sorted;
                all.push(last);
                return all;
            }, [])
            .compact()
            .value()
    );
}

const parser = new DOMParser();

export function unescapeHTML(html: string) {
    const parsedDoc = parser.parseFromString(`<html><body>${html}</body></html>`, 'text/html') as any as Document;
    return parsedDoc.documentElement.textContent;
}

export function getLinksFromUnescapeHTML(html: string) {
    const parsedDoc = parser.parseFromString(`<html><body>${html}</body></html>`, 'text/html') as any as Document;
    return [...parsedDoc.getElementsByTagName('a')].map((a) => a.href).filter(Boolean);
}

export async function getCdpAddressForHost(browserInstanceHost: string, timeout?: number) {
    // Lazily required to avoid circular dep (httpRequest requires logger which requires the utils to get the runnerVersion)
    const httpRequest = await import('../commons/httpRequest');
    try {
        interface Response {
            /** @example "Chrome/81.0.4044.138" */
            Browser: string;
            /** @example "1.3" */
            'Protocol-Version': string;
            /** @example "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_4) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/81.0.4044.138 Safari/537.36" */
            'User-Agent': string;
            /** @example "8.1.307.32" */
            'V8-Version': string;
            /** @example "537.36 (@8c6c7ba89cc9453625af54f11fd83179e23450fa)" */
            'WebKit-Version': string;
            /** @example "ws://localhost:58938/devtools/browser/d4290379-ec08-4d03-a41a-ab9d9d4c36ac" */
            webSocketDebuggerUrl: string;
        }
        const debuggerAddress = await httpRequest.get<Response>(
            `http://${browserInstanceHost}/json/version`,
            undefined,
            undefined,
            timeout,
        );
        return debuggerAddress.webSocketDebuggerUrl;
    } catch {
        throw new Error('unable to connect to devtools server');
    }
}

export function maskSecret(secret: string | null | undefined, { visiblePrefixLength = 1, visibleSuffixLength = 0, maskCharacter = '*' } = {}) {
    if (!secret?.length) {
        return '';
    }
    const MINIMUM_MASK_RATIO = 0.8; // What is the shortest valid mask out of the entire secret. if the affixes are longer than this, we mask the whole secret
    const maskedSubstringLength = secret.length - (visiblePrefixLength + visibleSuffixLength);
    if (maskedSubstringLength < (secret.length * MINIMUM_MASK_RATIO)) {
        return ''.padEnd(secret.length, maskCharacter);
    }
    const prefix = secret.slice(0, visiblePrefixLength); // take the prefix we don't want to mask
    const suffix = secret.slice(secret.length - visibleSuffixLength); // take the suffix we don't want to mask
    // Add masked characters between the prefix and suffix (we don't want to return a string that can indicate the exact length of the secret)
    return `${prefix}${maskCharacter.repeat(Math.ceil(secret.length * 0.9))}${suffix}`; // 0.9 to mask the secret length
}

export function maskObjectFields(target: object, fieldsToMask: string[]) {
    const fieldsSet = new Set(fieldsToMask);

    const maskRecursive = (obj: any): void => {
        if (obj === null || typeof obj !== 'object') {
            return;
        }

        if (Array.isArray(obj)) {
            obj.forEach(item => maskRecursive(item));
            return;
        }

        for (const key of Object.keys(obj)) {
            if (fieldsSet.has(key)) {
                const value = obj[key];
                if (value !== null && value !== undefined) {
                    obj[key] = maskSecret(String(value));
                }
            } else if (typeof obj[key] === 'object') {
                maskRecursive(obj[key]);
            }
        }
    };

    maskRecursive(target);
    return target;
}

export * from './fsUtils';
export * from './argsUtils';
export * from './timeUtils';
export * from './stringUtils';
export * from './promiseUtils';
export * from './mobileUtils';
export * from './performanceUtils';
export * from './npmUtils';

export type ValueOf<T> = T[keyof T];
export type AwaitedReturnType<T extends (...args: any) => any> = Awaited<ReturnType<T>>;
export type Falsy = false | undefined | null | '' | 0;
export type ExcludesFalseyTypeGuard = <T>(x: T | Falsy) => x is T;
